<template>
  <proa-modal
    :modal="modal"
    :title="title"
    @close="onClose"
    noDefaultBodyPadding
  >
    <div v-if="type === 'add' || type === 'inviteCafeRoom'" class="add-member-tab">
      <template v-if="designated">
        <div class="menu-wrapper">
          <div class="members">
            <div>
              <div v-for="user in designatedUsers" :key="user.id" class="member">
                <proa-check-box
                  :model="innerUserIds"
                  :value="user.id"
                  @change="newModel => { innerUserIds = newModel }"
                >
                  <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
                  <span class="ml-2 pl-1">{{ user.meta.displayName }}</span>
                </proa-check-box>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="tab-wrapper">
          <div
            v-for="(menu, index) in addMemberTabMenus"
            :key="index"
            :class="['tab-menu', { 'active': index == addMemberTabIndex }]"
            @click="addMemberTabIndex = index"
          >
            {{ $t(`common.modals.selectMemberModal.add.tabMenus.${menu}`) }}
          </div>
        </div>
        <div class="menu-wrapper">
          <div class="members">
            <div v-if="addMemberTabMenus[addMemberTabIndex] === 'pickup'">
              <div v-for="user in favoriteUsers" :key="user.id" class="member">
                <proa-check-box
                  :model="innerUserIds"
                  :value="user.id"
                  :disabled="reachedUpperLimit && !innerUserIds.includes(user.id)"
                  @change="newModel => { innerUserIds = newModel }"
                >
                  <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
                  <span class="ml-2 pl-1">{{ user.meta.displayName }}</span>
                </proa-check-box>
              </div>
            </div>
            <div v-else-if="addMemberTabMenus[addMemberTabIndex] === 'other'">
              <div v-for="user in otherUsers" :key="user.id" class="member">
                <proa-check-box
                  :model="innerUserIds"
                  :value="user.id"
                  :disabled="reachedUpperLimit && !innerUserIds.includes(user.id)"
                  @change="newModel => { innerUserIds = newModel }"
                >
                  <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
                  <span class="ml-2 pl-1">{{ user.meta.displayName }}</span>
                </proa-check-box>
              </div>
            </div>
            <div v-else-if="addMemberTabMenus[addMemberTabIndex] === 'guest' && currentLicensedPlan.availableAddTemporaryGuest" class="guest-tab">
              <div class="add-guest-member-button-wrapper">
                <proa-button
                  :disabled="reachedUpperLimit"
                  @click.native="addGuestMember"
                >
                  {{ $t('common.modals.selectMemberModal.add.addGuestMember') }}
                </proa-button>
              </div>
              <div class="guest-members-wrapper">
                <div
                  v-for="(guestMember, i) in innerGuestMembers"
                  :key="i"
                  class="guest-member"
                >
                  <proa-avatar
                    :src="require('@/assets/images/avatar_default.png')"
                    size="sm"
                    class="guest-avatar"
                    guest
                  />
                  <div class="input-wrapper">
                    <proa-input
                      v-model="guestMember.email"
                      type="email"
                      :invalid="!!guestMember.errors.email"
                      :placeholder="$t('models.user.attributes.email')"
                      dark
                      @input.native="clearGuestMemberError('email', i)"
                    />
                    <div v-if="guestMember.errors.email" class="invalid">
                      {{ guestMember.errors.email }}
                    </div>
                  </div>
                  <div class="input-wrapper">
                    <proa-input
                      v-model="guestMember.lastName"
                      type="text"
                      :invalid="!!guestMember.errors.lastName"
                      :placeholder="$t('models.user.attributes.displayName')"
                      dark
                      @input.native="clearGuestMemberError('lastName', i)"
                    />
                    <div v-if="guestMember.errors.lastName" class="invalid">
                      {{ guestMember.errors.lastName }}
                    </div>
                  </div>
                  <proa-button
                    @click.native="onClickDeleteGuestMember(i)"
                  >
                    {{ $t("common.deleteShort") }}
                  </proa-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div v-else class="delete-member-wrapper">
      <div class="menu-wrapper">
        <div class="members">
          <div v-for="user in assignedUsers" :key="user.id" class="member">
            <proa-check-box
              :model="innerUserIds"
              :value="user.id"
              :disabled="disabledUserIds.includes(user.id)"
              @change="newModel => { innerUserIds = newModel }"
            >
              <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
              <span class="ml-2 pl-1">{{ user.meta.displayName }}</span>
            </proa-check-box>
          </div>
          <div v-for="guestMember in guestMembers" :key="guestMember.email" class="member">
            <proa-check-box
              :model="innerGuestMembers.map((innerGuestMember) => innerGuestMember.email)"
              :value="guestMember.email"
              @change="onChangeGuestMembers"
            >
              <proa-avatar :src="require('@/assets/images/avatar_default.png')" size="sm" guest/>
              <span class="ml-2 pl-1">
                <span>{{ guestMember.lastName }} </span>
                <span class="text-secondary">{{ guestMember.firstName }}</span>
                <span> / {{ guestMember.email }}</span>
              </span>
            </proa-check-box>
          </div>
        </div>
      </div>
    </div>
    <template slot="footer">
      <proa-button @click.native="onClose" class="default-button-width mr-2">
        {{ $t('common.cancel') }}
      </proa-button>
      <proa-button
        v-if="type === 'inviteCafeRoom'"
        @click.native="onConfirm"
        class="default-button-width"
        variant="primary"
      >
        {{ $t('common.ok') }}
      </proa-button>
      <proa-button
        v-else-if="type === 'add'"
        @click.native="onConfirm"
        class="default-button-width"
      >
        {{ $t('common.add') }}
      </proa-button>
      <proa-button
        v-else
        @click.native="onConfirm"
        class="default-button-width"
      >
        {{ $t('common.delete') }}
      </proa-button>
    </template>
  </proa-modal>
</template>

<script>
import i18n from '@/i18n'
import { mapGetters } from 'vuex'
import User from '@/store/models/User'

export default {
  name: 'SelectMemberModal',
  props: {
    modal: {
      type: Boolean,
      default: false
    },
    designated: {
      type: Boolean,
      default: false
    },
    guestable: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'add' // NOTE: add or delete or inviteCafeRoom
    },
    userIds: {
      type: Array,
      default: () => []
    },
    guestMembers: {
      type: Array,
      default: () => []
    },
    disabledUserIds: {
      type: Array,
      default: () => []
    },
    designatedUserIds: {
      type: Array,
      default: () => []
    },
    upperLimit: {
      type: Number,
      default: null
    },
  },
  computed: {
    title: function() {
      let title = ''
      switch (this.type) {
        case 'add': {
          title = this.$t('common.modals.selectMemberModal.add.title')
          break
        }
        case 'delete': {
          title = this.$t('common.modals.selectMemberModal.delete.title')
          break
        }
        case 'inviteCafeRoom': {
          title = this.$t('common.modals.selectMemberModal.inviteCafeRoom.title')
          break
        }
      }
      return title
    },
    addMemberTabMenus: function() {
      let defaultTabMenus = [
        'pickup',
        'other'
      ]
      if (this.guestable && this.currentLicensedPlan.availableAddTemporaryGuest) {
        defaultTabMenus.push('guest')
      }
      return defaultTabMenus
    },
    assignedUsers: function() {
      return User.query().where((user) => {
        return this.userIds.includes(user.id) && (this.designated || this.signedInUser.id != user.id)
      }).get()
    },
    favoriteUsers: function() {
      return this.signedInUser.favoriteUsers.slice().sort((first, second) => {
        return first.listSequenceNo - second.listSequenceNo
      }).map(favoriteUser => {
        return User.find(favoriteUser.userId)
      }).filter(favoriteUser => favoriteUser != null && !this.userIds.includes(favoriteUser.id))
    },
    otherUsers: function() {
      const favoriteUserIds = this.signedInUser.favoriteUsers.map(favoriteUser => favoriteUser.userId)
      return User.query().where((user) => {
        return !favoriteUserIds.includes(user.id) && !this.userIds.includes(user.id) && this.signedInUser.id != user.id
      }).get()
    },
    designatedUsers: function() {
      return User.query().where((user) => {
        return this.designatedUserIds.map(Number).includes(user.id) && !this.userIds.includes(user.id)
      }).get()
    },
    reachedUpperLimit: function() {
      return this.upperLimit != null && this.upperLimit <= this.innerUserIds.length + this.innerGuestMembers.length
    },
    ...mapGetters({
      signedInUser: 'auth/signedInUser',
      currentLicensedPlan: 'auth/currentLicensedPlan'
    })
  },
  data() {
    return {
      addMemberTabIndex: 0,
      innerUserIds: [],
      innerGuestMembers: []
    }
  },
  methods: {
    onClose: function() {
      this.$emit('close')
      this.addMemberTabIndex = 0
      this.innerUserIds = []
      this.innerGuestMembers = []
    },
    onConfirm: function() {
      if (this.type === 'add' && this.guestable && !this.validateAddGuestMembers()) {
        this.addMemberTabIndex = this.addMemberTabMenus.indexOf('guest')
        return
      }
      this.$emit('confirm', {
        userIds: this.innerUserIds,
        guestUsers: this.innerGuestMembers.map((guestMember) => {
          // NOTE: errorsを除去
          return {
            id: guestMember.id,
            email: guestMember.email,
            firstName: guestMember.firstName,
            lastName: guestMember.lastName
          }
        })
      })
      this.innerUserIds = []
      this.innerGuestMembers = []
    },
    onClickDeleteGuestMember: function(index) {
      this.innerGuestMembers.splice(index, 1)
    },
    onChangeGuestMembers: function(emails) {
      this.innerGuestMembers = this.guestMembers.filter((guestMember) => {
        return emails.includes(guestMember.email)
      })
    },
    addGuestMember: function() {
      this.innerGuestMembers.push({
        email: '',
        firstName: i18n.tc('common.modals.selectMemberModal.add.addGuestMemberAutoFirstName'),
        lastName: '',
        errors: {}
      })
    },
    clearGuestMemberError: function(key, index) {
      if (!this.innerGuestMembers[index].errors[key]) return
      this.innerGuestMembers = this.innerGuestMembers.map((guestMember, i) => {
        if (i == index) {
          guestMember.errors[key] = null
        }
        return guestMember
      })
    },
    validateAddGuestMembers: function() {
      let invalid = false
      this.innerGuestMembers = this.innerGuestMembers.map((guestMember) => {
        if (!guestMember.lastName) {
          guestMember.errors.lastName = i18n.tc('common.modals.selectMemberModal.add.addGuestMemberErrorMessages.blank')
          invalid = true
        }
        if (!guestMember.email) {
          guestMember.errors.email = i18n.tc('common.modals.selectMemberModal.add.addGuestMemberErrorMessages.blank')
          invalid = true
        } else if (!/^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/.test(guestMember.email)) {
          guestMember.errors.email = i18n.tc('common.modals.selectMemberModal.add.addGuestMemberErrorMessages.invalidEmailFormat')
          invalid = true
        } else if (User.query().where('email', guestMember.email).exists()) {
          guestMember.errors.email = i18n.tc('common.modals.selectMemberModal.add.addGuestMemberErrorMessages.duplicateEmailInOrganization')
          invalid = true
        } else if (
          this.innerGuestMembers.filter((gm) => gm.email === guestMember.email).length >= 2 ||
          this.guestMembers.filter((gm) => gm.email === guestMember.email).length >= 1
        ) {
          guestMember.errors.email = i18n.tc('common.modals.selectMemberModal.add.addGuestMemberErrorMessages.duplicateEmailInReservation')
          invalid = true
        }
        // TODO: 既に反映されているゲストメンバーも照会
        return guestMember
      })
      return !invalid
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.add-member-tab, .delete-member-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;

  .tab-wrapper {
    display: flex;
    border-bottom: 1px solid $dark-border-color;

    .tab-menu {
      font-size: 13px;
      font-weight: 600;
      color: $light-text-color;
      border-bottom: 4px solid $light-gray-color;
      padding: 12px 15px;
      cursor: pointer;
      word-break: keep-all;

      &:first-child {
        margin-left: 15px;
      }

      &.active {
        color: $primary-color;
        border-bottom: 4px solid $primary-color;
      }
    }
  }
}

.menu-wrapper {
  flex-grow: 1;
  position: relative;

  .members {
    position: absolute;
    width: 100%;
    height: 100%;
    padding: 0 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;

    .member {
      margin-bottom: 10px;

      &:first-child::before {
        content: '';
        display: block;
        height: 20px;
      }

      &:last-child::after {
        content: '';
        display: block;
        height: 20px;
      }
    }

    .guest-tab {
      .add-guest-member-button-wrapper {
        position: sticky;
        top: 0;

        &:before {
          content: "";
          display: block;
          width: 100%;
          height: 20px;
          background-color: white;
        }

        button {
          width: 122px !important;
          height: 34px !important;
          padding: 0 !important;
        }
      }

      .guest-members-wrapper {
        margin: 20px 0;

        .guest-member {
          margin-bottom: 15px;
          display: flex;
          align-items: flex-start;
          position: relative;

          .guest-avatar {
            margin: -3px 15px 0 0;
          }

          .input-wrapper {
            flex-grow: 1;
            margin-right: 5px;

            input {
              font-size: 14px !important;
              height: 34px !important;

              &::placeholder {
                color: $light-text-color;
              }
            }

            .invalid {
              font-size: 12px;
              color: $error-color;
            }
          }
        }

        button {
          width: 56px !important;
          height: 34px !important;
          padding: 0 !important;
          margin-left: 10px;
        }
      }
    }
  }
}
</style>
